import logo from '../images/logo/logo.png'
import { v4 as uuidv4 } from 'uuid'
import { toaster } from '@/components/lib/Toast'
import api from '@/api'

export const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
export const ALLOWED_MESSAGE_FILE_TYPES = '.pdf,.jpg,.jpeg,.png,.gif,.webp,.docx'
export const TEAM_PERMISSIONS = [
  { key: 'users.create', description: 'Create and invite users' },
  { key: 'users.delete', description: 'Delete users' },
  { key: 'users.manage', description: 'Update users and manage permissions' },
  { key: 'trials.create', description: 'Create trials' },
  { key: 'trials.manage', description: 'Manage any trial' },
  { key: 'team.access', description: 'Access the team account pages' },
  { key: 'team.manage', description: 'Manage the team account' }
]
export const TRIAL_PERMISSIONS = [
  // We don't include trial.access as it's more of a system permission
  { key: 'trial.admin', description: 'Trial administrator (full access) (no-PII)' },
  { key: 'trial.read', description: 'Trial-wide admin view access (no-PII)' },
  { key: 'trial.write', description: 'Trial-wide admin write access' },
  { key: 'trial.readParticipants', description: 'List all participants in the trial' },
  { key: 'trial.registerParticipants', description: 'Register new participants into the trial' },
  { key: 'trial.writeParticipants', description: 'Edit participant details (name, email, phone, ID)' },
  { key: 'trial.allocateParticipants', description: 'Mark participants as allocated or unallocated' },
  { key: 'trial.archiveParticipants', description: 'Archive and unarchive participants' },
  { key: 'trial.withdrawParticipants', description: 'Mark participants as withdrawn' },
  { key: 'trial.deleteParticipants', description: 'Permanently delete participants and their data' },
  { key: 'trial.tagParticipants', description: 'Add and remove participants from groups (tags)' },
  { key: 'trial.rescheduleParticipants', description: 'Manually reschedule participant stages and tasks on the timeline' },
  { key: 'trial.readParticipantPersonalInformation', description: 'View participant personal profile and contact details (PII)' },
  { key: 'trial.readParticipantConsent', description: 'View a participant\'s consent information (PII)' },
  { key: 'trial.readTrialConsents', description: 'List all trial consents (PII)' },
  { key: 'trial.readConsentSettings', description: 'View-only access to consent settings (availability and informed consent forms)' },
  { key: 'trial.readTrialSettings', description: 'View-only access to trial settings and the About tab' },
  { key: 'trial.readDataTypes', description: 'View-only access to the Data Types configuration' },
  { key: 'trial.readStages', description: 'View-only access to the Stages configuration' },
  { key: 'trial.readParticipantNotes', description: 'View participant notes and communication logs' },
  { key: 'trial.writeParticipantNotes', description: 'Add notes to participants' },
  { key: 'trial.readParticipantStudyData', description: 'View participant study data (excluding restricted and PII fields)' },
  { key: 'trial.readParticipantStudyDataRestricted', description: 'View restricted participant study data fields (restricted)' },
  { key: 'trial.readParticipantStudyDataPii', description: 'View PII participant study data fields (PII)' },
  { key: 'trial.writeParticipantStudyData', description: 'Create & update participant study data' },
  { key: 'trial.exportData', description: 'Export / bulk-download trial data from the Results tab' },
  { key: 'trial.readFiles', description: 'View all files in the eTMF' },
  { key: 'trial.readApprovedFiles', description: 'View approved files only in the eTMF' },
  { key: 'trial.readFileVersions', description: 'View file versions in the eTMF' },
  { key: 'trial.writeFiles', description: 'Create new files and versions in the eTMF' },
  { key: 'trial.deleteFiles', description: 'Delete all files and versions in the eTMF' },
  { key: 'trial.requestFileApproval', description: 'Request eTMF file approval' },
  { key: 'trial.approveFiles', description: 'Approve all eTMF files' },
  { key: 'trial.readUsers', description: 'View trial users' },
  { key: 'trial.support', description: 'Send & receive support messages' },
  { key: 'trial.readTasks', description: 'List all trial tasks' },
  { key: 'trial.writeTasks', description: 'Create and manage trial tasks' },
  { key: 'trial.readQueries', description: 'List all trial queries' },
  { key: 'trial.writeQueries', description: 'Raise trial queries' },
  { key: 'trial.readAutomaticQueries', description: 'View automatic trial queries' },
  { key: 'trial.writeAutomaticQueries', description: 'Create automatic trial queries' },
  { key: 'trial.readSites', description: 'View trial sites' },
  { key: 'trial.writeSites', description: 'Create & update trial sites' },
  { key: 'trial.listPacks', description: 'List & view packs' },
  { key: 'trial.writePacks', description: 'Create & update packs' },
  { key: 'trial.deletePacks', description: 'Delete packs' },
  { key: 'trial.assignPacks', description: 'List and assign available packs to participants' },
  { key: 'trial.createParticipantMeetings', description: 'Create online video call meetings with participants' },
  { key: 'trial.readParticipantMeetings', description: 'View online video call meetings with participants' },
  { key: 'trial.joinParticipantMeetings', description: 'Join participant video call meetings' },
  { key: 'trial.deleteParticipantMeetings', description: 'Delete online video call meetings with participants' },
  { key: 'trial.manageWallet', description: 'Manage (view and top-up) the trial wallet' },
  { key: 'trial.useAssistant', description: 'Use the trial assistant' },
  { key: 'trial.readInsights', description: 'View trial and participant insights' },
  { key: 'trial.readSiteMessages', description: 'Read all site message boards in the trial' },
  { key: 'trial.writeSiteMessages', description: 'Post to any site message board & broadcast to all sites' }
]
export const GROUP_PERMISSIONS = [
  { key: 'group.read', description: 'Read group participants & data' },
  { key: 'group.write', description: 'Read & change group participant data' }
]
export const SITE_PERMISSIONS = [
  { key: 'site.access', description: 'Be a site member (recommended for any site roles)' },
  { key: 'site.read', description: 'Site-wide admin view access' },
  { key: 'site.write', description: 'Site-wide admin write access' },
  { key: 'site.readParticipants', description: 'List all participants at the site' },
  { key: 'site.registerParticipants', description: 'Register new participants and add them to this site' },
  { key: 'site.readFiles', description: 'Read site files' },
  { key: 'site.readApprovedFiles', description: 'View approved site files only' },
  { key: 'site.readFileVersions', description: 'View site file versions' },
  { key: 'site.writeFiles', description: 'Create new site files and versions' },
  { key: 'site.deleteFiles', description: 'Delete site files and versions' },
  { key: 'site.requestFileApproval', description: 'Request site file approval' },
  { key: 'site.approveFiles', description: 'Approve site files' },
  { key: 'site.readUsers', description: 'View site personnel' },
  { key: 'site.listPacks', description: 'List & view packs' },
  { key: 'site.writePacks', description: 'Create & update packs' },
  { key: 'site.assignPacks', description: 'List and assign available packs to participants' },
  { key: 'site.readSiteMessages', description: 'Read this site\'s message board' },
  { key: 'site.writeSiteMessages', description: 'Post to this site\'s message board' }
]

const utils = {
  uuidv4,
  /**
   * `get_trial_stages` turns recurring stages into multiple list rows and, for recurrence > 0,
   * rewrites `_id` to `mongoId::recurrence` so each row is unique in the UI. Backend calls expect
   * the real `stages` collection id and pass recurrence separately (`stageRecurrence`); this returns
   * that id by stripping any `::…` suffix.
   */
  timelineStageMongoId (stage) {
    const s = String(stage?._id ?? '')
    const i = s.indexOf('::')
    return i === -1 ? s : s.slice(0, i)
  },
  formatDate (date) {
    if (!date) return null
    const formatter = new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
    return formatter.format(new Date(date))
  },
  formatDateTime (date, { withTimezone, withSeconds } = {}) {
    if (!date) return null
    const formatter = new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: withSeconds ? '2-digit' : undefined,
      hour12: false
    })
    const formatted = formatter.format(new Date(date))
    if (withTimezone) {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
      return `${formatted} (${tz})`
    }
    return formatted
  },
  formatTime (timeStr) {
    if (!timeStr) return null
    const s = String(timeStr).trim()
    const parts = s.split(':')
    const h = parseInt(parts[0], 10) || 0
    const m = parseInt(parts[1], 10) || 0
    const sec = parseInt(parts[2], 10) || 0
    const formatter = new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: parts[2] ? '2-digit' : undefined,
      hour12: false
    })
    return formatter.format(new Date(1970, 0, 1, h, m, sec))
  },
  isSameDay (date1, date2) {
    return date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
  },
  toast (style, title, description) {
    toaster.create({
      title,
      description,
      type: style,
      duration: 5000
    })
  },
  hasFeature (team, feature) {
    return team?.enabledFeatures?.indexOf(feature) > -1
  },
  copyToClipboard (text) {
    navigator.clipboard.writeText(text).then(
      () => utils.toast('success', 'Copied to clipboard'),
      err => utils.toast('error', `Error copying: ${err}`)
    )
  },
  hasPermission (user, permission, scope) {
    return user?.permissions && (
      user.permissions.global?.indexOf('root') > -1 ||
      user.permissions.global?.indexOf(permission) > -1 ||
      (user.permissions[scope] && user.permissions[scope].indexOf(permission) > -1)
    )
  },
  canManageTeam (user, team) {
    return utils.hasPermission(user, 'team.manage', team?._id)
  },
  canManageTrial (user, trial) {
    return utils.hasPermission(user, 'trials.manage', trial?.team) || utils.hasPermission(user, 'trial.admin', trial?._id)
  },
  canManageTrialWallet (user, trial) {
    return utils.canManageTrial(user, trial) || utils.hasPermission(user, 'trial.manageWallet', trial?._id)
  },
  // A user can register participants if they can manage the trial, hold the
  // trial-level permission, or hold the site-level permission on any of the
  // trial's sites (new participants are added to that site server-side).
  canRegisterParticipants (user, trial, sites) {
    if (utils.canManageTrial(user, trial) || utils.hasPermission(user, 'trial.registerParticipants', trial?._id)) return true
    return (sites || []).some(s => utils.hasPermission(user, 'site.registerParticipants', s?._id))
  },
  canUseTrialAssistant (user, trial) {
    return utils.hasPermission(user, 'trial.useAssistant', trial?._id)
  },
  canReadTrialInsights (user, trial) {
    return utils.canReadTrial(user, trial) || utils.hasPermission(user, 'trial.readInsights', trial?._id)
  },
  canWriteTrial (user, trial) {
    return utils.canManageTrial(user, trial) || utils.hasPermission(user, 'trial.write', trial._id)
  },
  canReadTrial (user, trial) {
    return utils.canWriteTrial(user, trial) || utils.hasPermission(user, 'trial.read', trial._id)
  },
  // Viewing study data (per-participant Responses and the trial-wide Results
  // dashboards). Exporting is a separate, more sensitive action (see below).
  canReadParticipantStudyData (user, trial) {
    return utils.canReadTrial(user, trial) || utils.hasPermission(user, 'trial.readParticipantStudyData', trial?._id)
  },
  canExportData (user, trial) {
    return utils.canReadTrial(user, trial) || utils.hasPermission(user, 'trial.exportData', trial?._id)
  },
  // Participant-write capabilities. Each is independently grantable but still
  // implied by the broad trial.write / trial.admin roles (mirrors the API's
  // can_write_participants / can_allocate_participants / … helpers).
  canWriteParticipants (user, trial) {
    return utils.canWriteTrial(user, trial) || utils.hasPermission(user, 'trial.writeParticipants', trial?._id)
  },
  canAllocateParticipants (user, trial) {
    return utils.canWriteTrial(user, trial) || utils.hasPermission(user, 'trial.allocateParticipants', trial?._id)
  },
  canArchiveParticipants (user, trial) {
    return utils.canWriteTrial(user, trial) || utils.hasPermission(user, 'trial.archiveParticipants', trial?._id)
  },
  canWithdrawParticipants (user, trial) {
    return utils.canWriteTrial(user, trial) || utils.hasPermission(user, 'trial.withdrawParticipants', trial?._id)
  },
  canDeleteParticipants (user, trial) {
    // Destructive: implied by admin, not merely write.
    return utils.canManageTrial(user, trial) || utils.hasPermission(user, 'trial.deleteParticipants', trial?._id)
  },
  canTagParticipants (user, trial) {
    return utils.canWriteTrial(user, trial) || utils.hasPermission(user, 'trial.tagParticipants', trial?._id)
  },
  canRescheduleParticipants (user, trial) {
    return utils.canWriteTrial(user, trial) || utils.hasPermission(user, 'trial.rescheduleParticipants', trial?._id)
  },
  canReadParticipantPersonalInformation (user, trial) {
    return utils.hasPermission(user, 'trial.readParticipantPersonalInformation', trial._id)
  },
  canReadParticipantConsent (user, trial) {
    return utils.hasPermission(user, 'trial.readParticipantConsent', trial._id)
  },
  canReadTasks (user, trial) {
    return utils.canReadTrial(user, trial) || utils.hasPermission(user, 'trial.readTasks', trial._id)
  },
  canReadTrialConsents (user, trial) {
    return utils.hasPermission(user, 'trial.readTrialConsents', trial._id)
  },
  // View-only config tiers. Each is independently grantable but still implied by
  // the broad trial.read admin view (mirrors the API read helpers). Falling
  // through to canReadTrial means every user who can see these tabs today keeps
  // seeing them; the keys only add the ability to grant narrow view access.
  canReadTrialSettings (user, trial) {
    return utils.canReadTrial(user, trial) || utils.hasPermission(user, 'trial.readTrialSettings', trial?._id)
  },
  canReadDataTypes (user, trial) {
    return utils.canReadTrial(user, trial) || utils.hasPermission(user, 'trial.readDataTypes', trial?._id)
  },
  canReadStages (user, trial) {
    return utils.canReadTrial(user, trial) || utils.hasPermission(user, 'trial.readStages', trial?._id)
  },
  canReadConsentSettings (user, trial) {
    return utils.canReadTrial(user, trial) || utils.hasPermission(user, 'trial.readConsentSettings', trial?._id)
  },
  canReadTrialUsers (user, trial) {
    return utils.canReadTrial(user, trial) || utils.hasPermission(user, 'trial.readUsers', trial._id)
  },
  canReadTrialQueries (user, trial) {
    return utils.canReadTrial(user, trial) || utils.hasPermission(user, 'trial.readQueries', trial._id)
  },
  canWriteTrialQueries (user, trial) {
    if (!user || !trial?._id) return false
    return utils.canWriteTrial(user, trial) || utils.hasPermission(user, 'trial.writeQueries', trial._id)
  },
  canAccessTrial (user, trial) {
    return utils.canReadTrial(user, trial) || utils.hasPermission(user, 'trial.access', trial._id)
  },
  canSupportTrial (user, trial) {
    return utils.canManageTrial(user, trial) || utils.hasPermission(user, 'trial.support', trial._id)
  },
  canWriteTrialFiles (user, trial) {
    return utils.canManageTrial(user, trial) || utils.hasPermission(user, 'trial.writeFiles', trial._id)
  },
  canWriteSiteFiles (user, trial, site) {
    return utils.canWriteTrialFiles(user, trial) || utils.hasPermission(user, 'site.writeFiles', site?._id)
  },
  canReadTrialSiteMessages (user, trial) {
    // Write access implies read access (mirrors the API's can_read_trial_site_messages).
    return utils.canWriteTrialSiteMessages(user, trial) || utils.canReadTrial(user, trial) || utils.hasPermission(user, 'trial.readSiteMessages', trial?._id)
  },
  canWriteTrialSiteMessages (user, trial) {
    return utils.canWriteTrial(user, trial) || utils.hasPermission(user, 'trial.writeSiteMessages', trial?._id)
  },
  canReadSiteMessages (user, trial, site) {
    if (!user || !trial || !site) return false
    // Site-wide admin view (site.read, via canReadSite) also grants board access.
    return utils.canReadTrialSiteMessages(user, trial) || utils.canWriteSiteMessages(user, trial, site) || utils.canReadSite(user, site) || utils.hasPermission(user, 'site.readSiteMessages', site._id)
  },
  canWriteSiteMessages (user, trial, site) {
    if (!user || !trial || !site) return false
    // Site-wide admin write (site.write) implies posting to that site's board.
    return utils.canWriteTrialSiteMessages(user, trial) || utils.canWriteSite(user, site) || utils.hasPermission(user, 'site.writeSiteMessages', site._id)
  },
  canWriteFile (user, trial, file) {
    return utils.canWriteTrialFiles(user, trial) || utils.hasPermission(user, 'trial.writeFiles', file.trial) || utils.hasPermission(user, 'site.writeFiles', file.site)
  },
  canDeleteFile (user, trial, file) {
    return utils.canManageTrial(user, trial) || utils.hasPermission(user, 'trial.deleteFiles', file.trial) || utils.hasPermission(user, 'site.deleteFiles', file.site)
  },
  canApproveFile (user, trial, file) {
    return utils.canManageTrial(user, trial) || utils.hasPermission(user, 'trial.approveFiles', file?.trial) || utils.hasPermission(user, 'site.approveFiles', file?.site)
  },
  canReadFileVersions (user, trial, file) {
    if (!user || !trial || !file) return false
    if (utils.canReadTrial(user, trial)) return true
    if (utils.hasPermission(user, 'trial.readFileVersions', trial._id)) return true
    if (file.site && utils.hasPermission(user, 'site.readFileVersions', file.site)) return true
    return false
  },
  canRequestFileApproval (user, trial, file) {
    return utils.canManageTrial(user, trial) || utils.hasPermission(user, 'trial.requestFileApproval', file.trial) || utils.hasPermission(user, 'site.requestFileApproval', file.site)
  },
  canReadTrialSites (user, trial) {
    return utils.canReadTrial(user, trial) || utils.hasPermission(user, 'trial.readSites', trial._id)
  },
  canWriteTrialSites (user, trial) {
    return utils.canWriteTrial(user, trial) || utils.hasPermission(user, 'trial.writeSites', trial._id)
  },
  canWriteGroup (user, group) {
    return utils.hasPermission(user, 'group.write', group?._id)
  },
  canReadGroup (user, group) {
    return utils.canWriteGroup(user, group) || utils.hasPermission(user, 'group.read', group?._id)
  },
  canWriteSite (user, site) {
    return utils.hasPermission(user, 'site.write', site?._id)
  },
  canReadSite (user, site) {
    return utils.canWriteSite(user, site) || utils.hasPermission(user, 'site.read', site?._id)
  },
  canListPacksTrial (user, trial) {
    return utils.canManageTrial(user, trial) || utils.hasPermission(user, 'trial.listPacks', trial?._id)
  },
  canWritePacksTrial (user, trial) {
    return utils.canManageTrial(user, trial) || utils.hasPermission(user, 'trial.writePacks', trial?._id)
  },
  canDeletePacksTrial (user, trial) {
    return utils.canManageTrial(user, trial) || utils.hasPermission(user, 'trial.deletePacks', trial?._id)
  },
  canAssignPacksTrial (user, trial) {
    return utils.canManageTrial(user, trial) || utils.hasPermission(user, 'trial.assignPacks', trial?._id)
  },
  canListPacksSite (user, site) {
    return utils.hasPermission(user, 'site.listPacks', site?._id)
  },
  canWritePacksSite (user, site) {
    return utils.hasPermission(user, 'site.writePacks', site?._id)
  },
  canDeletePacksSite (user, site) {
    return utils.hasPermission(user, 'site.deletePacks', site?._id)
  },
  canAssignPacksSite (user, site) {
    return utils.hasPermission(user, 'site.assignPacks', site?._id)
  },
  canListPacksForParticipant (user, trial, participant) {
    if (!user || !trial || !participant) return false
    if (utils.canListPacksTrial(user, trial)) return true
    for (const siteId of (participant.sites ?? [])) {
      if (utils.canListPacksSite(user, { _id: siteId })) return true
    }
    return false
  },
  canAssignPacksForParticipant (user, trial, participant) {
    if (!user || !trial || !participant) return false
    if (utils.canAssignPacksTrial(user, trial)) return true
    for (const siteId of (participant.sites ?? [])) {
      if (utils.canAssignPacksSite(user, { _id: siteId })) return true
    }
    return false
  },
  canCreateParticipantMeetings (user, trial) {
    if (!user || !trial) return false
    return utils.canWriteTrial(user, trial) || utils.hasPermission(user, 'trial.createParticipantMeetings', trial?._id)
  },
  canReadParticipantMeetings (user, trial) {
    if (!user || !trial) return false
    return utils.canReadTrial(user, trial) || utils.hasPermission(user, 'trial.readParticipantMeetings', trial?._id)
  },
  canJoinParticipantMeetings (user, trial) {
    if (!user || !trial) return false
    return utils.canReadTrial(user, trial) || utils.hasPermission(user, 'trial.joinParticipantMeetings', trial?._id)
  },
  canDeleteParticipantMeetings (user, trial) {
    if (!user || !trial) return false
    return utils.canWriteTrial(user, trial) || utils.hasPermission(user, 'trial.deleteParticipantMeetings', trial?._id)
  },
  canReadParticipantNotes (user, trial) {
    if (!user || !trial) return false
    return utils.canReadTrial(user, trial) || utils.hasPermission(user, 'trial.readParticipantNotes', trial?._id)
  },
  canWriteParticipantNotes (user, trial) {
    if (!user || !trial) return false
    return utils.canWriteTrial(user, trial) || utils.hasPermission(user, 'trial.writeParticipantNotes', trial?._id)
  },
  canWriteParticipant (user, trial, participant) {
    if (!user || !trial || !participant) return false
    if (utils.canWriteTrial(user, trial)) return true
    for (const group of (participant.groups ?? [])) {
      if (utils.canWriteGroup(user, { _id: group })) return true
    }
    for (const site of (participant.sites ?? [])) {
      if (utils.canWriteSite(user, { _id: site })) return true
    }
    return false
  },
  canReadParticipant (user, trial, participant) {
    if (utils.canWriteParticipant(user, trial, participant)) return true
    if (!user || !trial || !participant) return false
    if (utils.canReadTrial(user, trial)) return true
    for (const group of (participant.groups ?? [])) {
      if (utils.canReadGroup(user, { _id: group })) return true
    }
    for (const site of (participant.sites ?? [])) {
      if (utils.canReadSite(user, { _id: site })) return true
    }
    return false
  },
  randomAlphaNumeric (length) {
    const o = '3456789ABCDEFGJKLMNPRSTUVWXY'
    let s = ''
    while (s.length < length) s += o[Math.floor(Math.random() * o.length)]
    return s
  },
  ensureHttp (s) {
    if (s && s.toLowerCase().indexOf('http') === -1) return `http://${s}`
    return s
  },
  activePath (pattern) {
    const match = window.location.pathname.match(new RegExp(pattern, 'i'))
    return match && match.length > 0
  },
  absoluteUrl (path) {
    return window.location.protocol + '//' + window.location.host + path
  },
  hasEmailSubscription (user, sub) {
    return user && user.subscriptions && user.subscriptions.email && user.subscriptions.email.indexOf(sub) > -1
  },
  teamLogo (team) {
    return (team && team.logoUrl) || logo
  },
  debounce (callback, delay) {
    let timer
    return (...args) => {
      clearTimeout(timer)
      timer = setTimeout(() => callback(...args), delay || 2000) // eslint-disable-line
    }
  },
  kToLbs (pK) {
    const nearExact = parseFloat(pK) / 0.45359237
    const lbs = Math.floor(nearExact)
    const oz = (nearExact - lbs) * 16
    return {
      pounds: lbs,
      ounces: oz
    }
  },
  lbToK (pounds, ounces) {
    const totalPounds = parseFloat(pounds) + parseFloat(ounces) / 16
    return totalPounds * 0.45359237
  },
  cmToFeet (cm) {
    const totalInches = parseFloat(cm) / 2.54 // 1 inch = 2.54 cm [web:2][web:8]
    const feet = Math.floor(totalInches / 12)
    const inches = +(totalInches % 12).toFixed(2) // round to 2 decimal places
    return { feet, inches }
  },
  feetToCm (feet, inches) {
    return parseInt(feet) * 30.48 + parseFloat(inches) * 2.54
  },
  celsiusToFahrenheit (c) {
    if (isNaN(parseFloat(c))) return
    return (parseFloat(c) * (9 / 5)) + 32
  },
  fahrenheitToCelsius (f) {
    if (isNaN(parseFloat(f))) return
    return (parseFloat(f) - 32) * (5 / 9)
  },
  shuffleArray (a) {
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]]
    }
    return a
  },
  getAutomaticMessageScheduleAsString (message, groups, anchors) {
    const s = message?.schedule || {}
    let scheduleString = ''
    if (s.frequency === 'specific') {
      scheduleString += `On: ${s.dates?.map(d => utils.formatDateTime(d, { withTimezone: true })).join(', ')}`
    } else if (s.frequency === 'daily') scheduleString += `Every day at ${s.hour}:00 (participant local time)`
    else if (s.frequency === 'weekly') scheduleString += `Every ${DAYS[s.day]} at ${s.hour}:00 (participant local time)`
    const messageGroup = groups?.find(g => g._id === s?.group)
    if (messageGroup) {
      scheduleString += ` (${messageGroup.name})`
    }
    // Anchor-relative messages are a separate sendWhen variant (not a schedule
    // frequency); display them as "N days after <anchor>, HH:00 participant local time".
    if (message?.sendWhen === 'anchor') {
      const offset = s.dayOffset ?? 0
      const anchorName = anchors?.find(a => a._id === s.anchor)?.name || 'the anchor'
      const h = Number(s.hour ?? 0)
      const hourLabel = `${h < 10 ? '0' : ''}${h}:00`
      scheduleString = `${Math.abs(offset)} day${Math.abs(offset) === 1 ? '' : 's'} ${offset < 0 ? 'before' : 'after'} ${anchorName}, ${hourLabel} (participant local time)`
      if (messageGroup) {
        scheduleString += ` (${messageGroup.name})`
      }
    }
    return scheduleString
  },
  downloadFile (url, fileName) {
    const element = document.createElement('a')
    element.setAttribute('href', url)
    element.setAttribute('download', fileName)
    element.setAttribute('target', '_blank')
    element.style.display = 'none'
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
  },
  uploadFile (file, forType, forId, onComplete, onError) {
    api.uploads.generateFileUploadRequest({
      forType,
      forId,
      name: file.name,
      size: file.size,
      type: file.type
    }, (response) => {
      const xhr = new window.XMLHttpRequest()
      xhr.open('PUT', response.signedRequest)
      xhr.setRequestHeader('Content-Type', file.type)
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            onComplete({ expectedUrl: response.expectedUrl, storedName: response.fileName, name: file.name, type: 'file' })
          } else if (onError) {
            onError('Unable to upload file')
          }
        }
      }
      xhr.send(file)
    }, (err) => {
      if (onError) onError(err.message || 'Unable to upload file')
    })
  },
  oauthLoginStateStorageKey (provider) {
    return `trialflare_oauth_login_state_${provider}`
  },
  randomOAuthState () {
    const a = new Uint8Array(32)
    window.crypto.getRandomValues(a)
    return Array.from(a, b => b.toString(16).padStart(2, '0')).join('')
  },
  loginWithGoogle () {
    const url = 'https://accounts.google.com/o/oauth2/v2/auth'
    const redirectUri = `${window.location.protocol}//${window.location.host}/login/oauth/google`
    const scope = 'openid email profile'
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
    const state = utils.randomOAuthState()
    window.sessionStorage.setItem(utils.oauthLoginStateStorageKey('google'), state)
    const q = new URLSearchParams({
      client_id: clientId,
      scope,
      redirect_uri: redirectUri,
      response_type: 'code',
      state
    })
    window.location = `${url}?${q.toString()}`
  },
  loginWithMicrosoft () {
    const url = 'https://login.microsoftonline.com/organizations/oauth2/v2.0/authorize'
    const redirectUri = `${window.location.protocol}//${window.location.host}/login/oauth/microsoft`
    const scope = 'openid email offline_access User.Read'
    const clientId = import.meta.env.VITE_MICROSOFT_CLIENT_ID
    const state = utils.randomOAuthState()
    window.sessionStorage.setItem(utils.oauthLoginStateStorageKey('microsoft'), state)
    const q = new URLSearchParams({
      client_id: clientId,
      scope,
      redirect_uri: redirectUri,
      response_type: 'code',
      state
    })
    window.location = `${url}?${q.toString()}`
  },
  connectToGoogle (scope, returnTo, serviceName) {
    const url = 'https://accounts.google.com/o/oauth2/v2/auth'
    const redirectUri = `${window.location.protocol}//${window.location.host}/connect/google`
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
    const callback = returnTo || window.location.pathname
    const state = JSON.stringify({ service: serviceName || 'google', scope, callback })
    window.location = `${url}?client_id=${clientId}&scope=${scope}&redirect_uri=${redirectUri}&response_type=code&state=${btoa(state)}&prompt=consent&access_type=offline`
  },
  connectToMicrosoft (scope, returnTo, serviceName) {
    const url = 'https://login.microsoftonline.com/organizations/oauth2/v2.0/authorize'
    const redirectUri = `${window.location.protocol}//${window.location.host}/connect/microsoft`
    const clientId = import.meta.env.VITE_MICROSOFT_CLIENT_ID
    const callback = returnTo || window.location.pathname
    const state = JSON.stringify({ service: serviceName || 'microsoft', scope, callback })
    window.location = `${url}?client_id=${clientId}&scope=${scope}&redirect_uri=${redirectUri}&response_type=code&state=${btoa(state)}&prompt=consent&access_type=offline`
  },
  connectToDropbox (scope, returnTo, serviceName) {
    const url = 'https://www.dropbox.com/oauth2/authorize'
    const redirectUri = `${window.location.protocol}//${window.location.host}/connect/dropbox`
    const clientId = import.meta.env.VITE_DROPBOX_CLIENT_ID
    const callback = returnTo || window.location.pathname
    const state = JSON.stringify({ service: serviceName || 'dropbox', scope, callback })
    window.location = `${url}?client_id=${clientId}&scope=${scope}&redirect_uri=${redirectUri}&response_type=code&state=${btoa(state)}&token_access_type=offline`
  }

}

/**
 * Evaluate a single condition against a response value
 * @param {Object} condition - The condition object with operator and value
 * @param {*} conditionResp - The response value to evaluate against
 * @returns {boolean} - True if condition is met
 */
/**
 * Pick out the one number a condition on a Likert should compare against.
 *
 * A Likert answer isn't a single value: it's an answer per statement, stored as
 * {statement key: score}. A condition compares one value against one threshold,
 * so we need a single number out of that set first. Which number depends on how
 * the condition was set up:
 *   - it names a statement -> that statement's own score
 *   - it names none        -> the total across the statements answered, the
 *                             same number scoring rules, calculations and
 *                             automatic queries all use
 *
 * Returns null when there is no number to compare (nothing answered yet, or the
 * named statement was answered "not applicable"), which no numeric comparison
 * then matches.
 */
export function likertConditionValue (condition, value, dataType) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  if (condition?.statement) return value[condition.statement] ?? null
  return likertTotal(value, dataType)
}

export function evaluateCondition (condition, conditionResp, dataType) {
  // A Likert answer is a score per statement, not one value, so pick out the
  // single number this condition is about: the named statement's score, or the
  // total across them.
  //
  // This has to happen before the BMI check below. That check identifies a BMI
  // answer by looking for a 'bmi' key rather than by checking the type — and a
  // Likert statement labelled "BMI" is stored under exactly that key. Left in
  // the other order it would mistake the matrix for a BMI answer and throw the
  // rest of it away.
  if (dataType?.type === 'likert') {
    if (condition?.operator === 'exists') {
      // "Answered" is a different question from "has a number to compare": a
      // statement answered "not applicable" is a deliberate answer that
      // contributes nothing to the total, so ask about the answers themselves.
      // Judged on which statements were answered, not on whether each answer
      // still sits on the scale, so this agrees with the server on a response
      // saved before a scale point's value was changed.
      const answered = likertAnswers(dataType, conditionResp)
      return condition.statement
        ? answered.some(a => a.key === condition.statement)
        : answered.length > 0
    }
    conditionResp = likertConditionValue(condition, conditionResp, dataType)
  }

  // Handle BMI type: extract the bmi value from the object
  if (conditionResp && typeof conditionResp === 'object' && !Array.isArray(conditionResp) && 'bmi' in conditionResp) {
    conditionResp = conditionResp.bmi
  }

  const exists =
    conditionResp !== null && conditionResp !== undefined && conditionResp !== '' &&
    (Array.isArray(conditionResp) ? (conditionResp?.length > 0) : true)

  const isEqual =
    conditionResp?.toString()?.toLowerCase()?.trim() === condition.value?.toString()?.toLowerCase()?.trim() ||
    (Array.isArray(condition?.value) && condition?.value.indexOf(conditionResp) > -1) ||
    (Array.isArray(conditionResp) && conditionResp.indexOf(condition?.value) > -1) ||
    (Array.isArray(condition?.value) && Array.isArray(conditionResp) && condition.value.filter(x => conditionResp?.indexOf(x) !== -1)?.length > 0)

  // `>` and `<` coerce in JavaScript, so a bare comparison reads an unanswered
  // field as 0 — `null < 3` is true, and the field would appear on screen while
  // the server, which treats "no value" as not met, discards whatever is typed
  // into it. Mirror the server's rule instead: anything Python's float() would
  // refuse (null, blank, text, a list, an object, a date) simply doesn't match.
  const asNumber = v => {
    if (v === null || v === undefined) return null
    if (typeof v === 'number') return Number.isFinite(v) ? v : null
    if (typeof v === 'boolean') return v ? 1 : 0
    if (typeof v !== 'string' || v.trim() === '') return null
    const parsed = Number(v)
    return Number.isFinite(parsed) ? parsed : null
  }
  const left = asNumber(conditionResp)
  const right = asNumber(condition.value)
  const comparable = left !== null && right !== null

  return (
    (condition.operator === 'exists' && exists) ||
    (condition.operator === 'equal' && isEqual) ||
    (condition.operator === 'notequal' && !isEqual) ||
    (condition.operator === 'more' && comparable && left > right) ||
    (condition.operator === 'less' && comparable && left < right)
  )
}

/**
 * Build a component visibility map that handles chained conditionals
 * Processes components in order and accounts for dependencies (e.g., C depends on B depends on A)
 * @param {Array} components - Array of component objects from the stage
 * @param {Object} response - Current response data (object mapping dataType to {value, ...})
 * @param {Object} existingResponse - Optional existing response data for fallback
 * @returns {Object} - Map of dataType_id to boolean indicating visibility
 */
export function buildComponentVisibilityMap (components, response, existingResponse = {}) {
  const visibilityMap = {}
  const mergedResponse = { ...existingResponse, ...response }

  // Process components in order (as they appear in the stage)
  components?.forEach(c => {
    if (!c.dataType) return

    const dataTypeId = c.dataType

    // If no condition, component is always visible
    if (!c.condition) {
      visibilityMap[dataTypeId] = true
      return
    }

    // Check if the dependency component is visible first (handles chained conditionals)
    const dependencyDataTypeId = c.condition.dataType
    if (dependencyDataTypeId in visibilityMap) {
      const dependencyVisible = visibilityMap[dependencyDataTypeId]
      if (!dependencyVisible) {
        // The component this depends on is not visible, so this component cannot be visible
        visibilityMap[dataTypeId] = false
        return
      }
    }

    // Get the condition response value
    const conditionResp = mergedResponse[dependencyDataTypeId]?.value

    // The data type of the field being branched on. Components carry their own
    // data type object on `type`, so the dependency's is already to hand and
    // doesn't need passing in separately.
    const dependencyDataType = components.find(c2 => c2.dataType === dependencyDataTypeId)?.type

    // Evaluate the condition
    const conditionMet = evaluateCondition(c.condition, conditionResp, dependencyDataType)
    visibilityMap[dataTypeId] = conditionMet
  })

  return visibilityMap
}

/**
 * Check if a component's condition is met using a pre-built visibility map
 * @param {Object} component - The component to check
 * @param {Object} visibilityMap - Pre-built visibility map from buildComponentVisibilityMap
 * @returns {boolean} - True if component should be visible
 */
export function componentConditionIsMet (component, visibilityMap) {
  if (!component.condition) return true
  if (!component.dataType) return true

  // Use the pre-computed visibility map which handles chained dependencies
  return visibilityMap[component.dataType] ?? true
}

/**
 * Format KYCAID applicant addresses for display (Consents table, participant Consent tab).
 * @param {Array} addresses - applicant.addresses from KYCAID payload
 * @returns {string} - Formatted address(es), newline-separated if multiple, or '—'
 */
export function formatKycaidAddresses (addresses) {
  if (!addresses?.length) return '—'
  return addresses.map(a => {
    const part = a.full_address || [a.street_name, a.building_number, a.city, a.state_or_province || a.region, a.postal_code, a.country].filter(Boolean).join(', ')
    return part || '—'
  }).join('\n')
}

/**
 * True if the chosen file looks like MP4 (MIME video/mp4 or .mp4 name).
 * Browsers may report an empty or generic MIME for some files; extension is a fallback.
 */
export function isLikelyMp4Upload ({ mimeType, name } = {}) {
  const mt = (mimeType || '').toLowerCase().trim()
  if (mt === 'video/mp4') return true
  const base = (name || '').toLowerCase().split(/[/\\]/).pop() || ''
  return base.endsWith('.mp4')
}

/**
 * The statement rows of a Likert matrix, in their configured order.
 * @param {Object} dataType - The Likert data type
 */
export function likertStatements (dataType) {
  return (dataType?.statements ?? []).filter(s => s?.key)
}

/**
 * A key for a new Likert statement.
 *
 * Generated where the statement is added, so it is fixed from that moment: the
 * answers collected against a statement are stored under its key, so re-wording
 * it or moving it up the list has to leave the key alone. The API checks it is
 * unique within the field and mints one for any statement that arrives without.
 */
export function newLikertStatementKey () {
  return `s_${uuidv4().replace(/-/g, '').slice(0, 12)}`
}

/**
 * A Likert response narrowed to the answers the field can still accept: only
 * statements it still has, answered either with a point on the current scale
 * or — where the field offers it — "not applicable".
 *
 * Answers are stored against a statement's key, so re-wording or reordering a
 * statement keeps them. Removing one, or changing what a scale point is worth,
 * does not: those answers stop counting everywhere, and the API drops them on
 * the next save. Narrowing here stops the field carrying them back into a
 * response being edited, where they would sit in the value the client submits
 * while being unreachable from the UI.
 * @param {Object} dataType - The Likert data type
 * @param {Object} value - The stored response value, keyed by statement key
 */
export function likertValidAnswers (dataType, value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const scaleValues = new Set((dataType?.scalePoints ?? []).map(p => p.value))
  const allowsNotApplicable = !!dataType?.includeNotApplicable
  const answers = {}
  for (const statement of likertStatements(dataType)) {
    if (!(statement.key in value)) continue
    const answer = value[statement.key]
    if (answer === null) {
      if (allowsNotApplicable) answers[statement.key] = null
    } else if (scaleValues.has(answer)) {
      answers[statement.key] = answer
    }
  }
  return answers
}

/**
 * Summarise a Likert response for display: one entry per statement that was
 * answered, in the data type's statement order, with the label of the scale
 * point chosen. A null answer means the participant chose "not applicable".
 * @param {Object} dataType - The Likert data type
 * @param {Object} value - The stored response value, keyed by statement key
 */
export function likertAnswers (dataType, value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return []
  const pointLabels = {}
  for (const point of dataType?.scalePoints ?? []) pointLabels[point.value] = point.label
  return likertStatements(dataType)
    .filter(statement => statement.key in value)
    .map(statement => {
      const answer = value[statement.key]
      return {
        key: statement.key,
        label: statement.label,
        answer,
        answerLabel: answer === null ? 'Not applicable' : (pointLabels[answer] ?? String(answer))
      }
    })
}

/**
 * The total across the answered statements of a Likert response, which is what
 * scoring rules and calculation formulas compare against. Null if nothing was
 * answered (statements marked "not applicable" don't count).
 * Pass the data type so answers against statements that have since been removed
 * are left out, exactly as they are from the columns shown alongside the total.
 * @param {Object} value - The stored response value, keyed by statement key
 * @param {Object} dataType - The Likert data type
 */
export function likertTotal (value, dataType) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const keys = likertStatements(dataType).map(s => s.key)
  const answered = Object.entries(value)
    .filter(([key, v]) => keys.includes(key) && typeof v === 'number')
    .map(([, v]) => v)
  if (!answered.length) return null
  return answered.reduce((total, v) => total + v, 0)
}

export default utils
